import type { Persister } from '@tanstack/query-persist-client-core'
import { isTauri } from '@/lib/utils/tauri'

import { createIndexedDBPersister } from './idb-persister'
import { createSQLitePersister } from './sqlite-persister'
import { createSerializedThrottledPersister } from './throttled-persister'

const PERSIST_THROTTLE_MS = 1500

export async function createPersister(
  scopeKey: string,
): Promise<Persister | null> {
  // Change the schema only when the persisted query shape becomes incompatible.
  // A deployment timestamp must not invalidate otherwise reusable offline data.
  const cacheKey = `scrambl-query-cache-${scopeKey}-schema-1`
  const scopePrefix = `scrambl-query-cache-${scopeKey}-`

  try {
    if (isTauri()) {
      return createSerializedThrottledPersister(
        await createSQLitePersister(cacheKey, scopePrefix),
        PERSIST_THROTTLE_MS,
      )
    }
    return createSerializedThrottledPersister(
      createIndexedDBPersister(cacheKey, scopePrefix),
      PERSIST_THROTTLE_MS,
    )
  } catch (e) {
    console.warn(
      '[QueryCache] Failed to create persister, running without cache',
      e,
    )
    return null
  }
}

/** Sign-out removes every organization snapshot belonging to this account. */
export async function removeOwnerCaches(userId: string): Promise<void> {
  const prefix = `scrambl-query-cache-${JSON.stringify([userId]).slice(0, -1)},`
  if (isTauri()) {
    const { removeSQLiteCaches } = await import('./sqlite-persister')
    await removeSQLiteCaches(prefix)
  } else {
    const { removeIndexedDBCaches } = await import('./idb-persister')
    await removeIndexedDBCaches(prefix)
  }
}
